import type { Replace } from 'types/replace';
import { Messages } from 'constants/messages';
import { showErrorToast } from 'components/Toast';
import type { AxiosError } from 'axios';
import api, { Api } from './api';
import { API_ERROR_CODES } from './api.errors';

type CustomAxiosError<T> = Replace<
  AxiosError<T>,
  'config',
  AxiosError['config'] & { _retry: boolean | undefined }
>;

async function refreshTokenData(this: Api, error: CustomAxiosError<any>) {
  if (
    error.response?.data?.code === API_ERROR_CODES.INCORRECT_USERNAME_OR_PASSWORD ||
    error.response?.data?.code === API_ERROR_CODES.ACCESS_DENIED ||
    error.response?.data?.code === API_ERROR_CODES.EMAIL_IS_NOT_RECOGNIZED
  ) {
    return Promise.reject(error);
  }

  if (!this.refreshToken) {
    this.logoutApp();
    return Promise.reject(error);
  }

  const originalConfig = error.config;
  if (!originalConfig) {
    return Promise.reject(error);
  }

  if (!Api._retry) {
    Api._retry = true;
    try {
      const { data: tokenData } = await this.refreshAccessToken(this.refreshToken);

      this.setTokens(tokenData.accessToken, tokenData.refreshToken);
      originalConfig.headers = originalConfig.headers ?? {};
      originalConfig.headers.Authorization = `Bearer ${tokenData.accessToken}`;

      Api.axios(originalConfig);
      Api._retry = false;
    } catch (error) {
      Api._retry = false;
      this.logoutApp();
      setTimeout(() => {
        showErrorToast(Messages.sessionExpiredError);
      }, 100);

      return Promise.reject(error);
    }
  }
}

export default async function (this: Api, error: CustomAxiosError<any>): Promise<any> {
  if (error.response === undefined) return Promise.reject(error);

  switch (error.response.status) {
    case 401:
      return await refreshTokenData.call(this, error);
    case 403:
      api.logoutBlockedUser(error);
      return;
    default:
      return Promise.reject(error);
  }
}
